import { saveAs } from "file-saver";
import { useMemo, useEffect, useState } from "react";
import { IoDocumentTextSharp, IoFilter } from "react-icons/io5";
import { LuLayoutGrid, LuCopy, LuCheck } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import WpDynamicTable from "../../Components/WhatsappTable/WpDynamicTable";
import { fetchLogReport } from "../../Redux/WhatsappSlice/LogReport";
import Loader from "../../Components/Loader/Loader";

const ResponseCell = ({ text, limit = 50, isAllExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isFormatted, setIsFormatted] = useState(false);

  const formattedJSON = useMemo(() => {
    if (!text || text === "-") return null;

    // 1. Try standard single JSON object/array parse
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === "object") {
        return JSON.stringify(parsed, null, 2);
      }
    } catch (e) { }

    // 2. Try single JSON parse with single-to-double quote substitution
    try {
      const normalized = text.replace(/'/g, '"');
      const parsed = JSON.parse(normalized);
      if (parsed && typeof parsed === "object") {
        return JSON.stringify(parsed, null, 2);
      }
    } catch (e) { }

    // 3. Try parsing concatenated JSON objects ({...}{...})
    try {
      const results = [];
      let braceCount = 0;
      let startIndex = -1;

      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (char === '{') {
          if (braceCount === 0) {
            startIndex = i;
          }
          braceCount++;
        } else if (char === '}') {
          braceCount--;
          if (braceCount === 0 && startIndex !== -1) {
            const jsonStr = text.substring(startIndex, i + 1);
            try {
              results.push(JSON.parse(jsonStr));
            } catch (e1) {
              try {
                const normalized = jsonStr.replace(/'/g, '"');
                results.push(JSON.parse(normalized));
              } catch (e2) { }
            }
            startIndex = -1;
          }
        }
      }

      if (results.length > 0) {
        if (results.length === 1) {
          return JSON.stringify(results[0], null, 2);
        }
        return JSON.stringify(results, null, 2);
      }
    } catch (e) { }

    return null;
  }, [text]);

  if (!text || text === "-") return "-";

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(isFormatted && formattedJSON ? formattedJSON : text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const activeText = isFormatted && formattedJSON ? formattedJSON : text;

  const charLimit = limit;
  const currentlyExpanded = isAllExpanded || isExpanded;
  const isTruncated = activeText.length > charLimit;
  const displayText = isTruncated ? (currentlyExpanded ? activeText : activeText.slice(0, charLimit) + "...") : activeText;

  return (
    <div className="text-left font-medium text-slate-700 leading-normal break-words relative group min-h-[24px] flex items-start pr-16 w-full py-1">
      <div className="w-full">
        {isFormatted && formattedJSON ? (
          <pre className={`bg-slate-50 border border-slate-200 rounded p-2 text-xs font-mono text-slate-800 overflow-x-auto whitespace-pre-wrap break-all scrollbar-thin ${currentlyExpanded ? "max-h-none overflow-y-visible" : "max-h-[300px] overflow-y-auto"}`}>
            {displayText}
          </pre>
        ) : (
          <div className={currentlyExpanded ? "whitespace-normal break-all text-slate-700 font-medium py-0.5" : "overflow-x-auto whitespace-nowrap scrollbar-thin max-w-full text-slate-700 font-medium py-0.5"}>
            {displayText}
          </div>
        )}
        {isTruncated && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="text-xs text-blue-600 hover:text-blue-800 font-bold ml-1.5 focus:outline-none cursor-pointer border-none bg-transparent inline-flex items-center"
          >
            {currentlyExpanded ? "Read Less" : "Read More"}
          </button>
        )}
      </div>

      <div className="absolute right-0 top-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        {formattedJSON && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsFormatted(!isFormatted);
            }}
            className={`px-1.5 py-1 rounded hover:bg-slate-100 transition-all text-[10px] font-extrabold border border-slate-300 bg-white cursor-pointer flex items-center justify-center shadow-sm ${isFormatted ? "text-blue-600 border-blue-400 bg-blue-50" : "text-slate-500 hover:text-slate-700"
              }`}
            title={isFormatted ? "Show raw response" : "Format JSON response"}
          >
            JSON
          </button>
        )}
        <button
          onClick={handleCopy}
          className="p-1.5 rounded hover:bg-slate-100 transition-all text-slate-400 hover:text-slate-700 focus:outline-none cursor-pointer border-none bg-transparent flex items-center justify-center"
          title="Copy to clipboard"
        >
          {copied ? (
            <LuCheck className="text-green-600" size={15} />
          ) : (
            <LuCopy size={15} />
          )}
        </button>
      </div>
    </div>
  );
};

const WhatsappLogReport = () => {
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear(); // Get the full 4-digit year
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const dispatch = useDispatch();

  const { LogData, loading: reduxLoading } = useSelector((state) => state.WpLogReport);

  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchLogReport());
        setDataLoaded(true);
      } catch (error) {
        console.error("Error fetching log data:", error);
        setDataLoaded(true);
      }
    };
    fetchData();
  }, [dispatch]);

  const [filters, setFilters] = useState({
    MobileUserName: "",
    WhaClientName: "",
    FMTID: "",
    UnqID: "",
    VoucherNo: "",
    IsSuccess: "",
    MobileNo: "",
    FirmName: "",
    ProjectName: "",
    StaticIP: "",
    DateFrom: "",
    DateTo: "",
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [expandAll, setExpandAll] = useState(false);

  const filterdata = useMemo(() => {
    return LogData?.filter((item) => {
      const itemDate = new Date(item.WhaSendDatetime);
      const dateTo = filters.DateTo ? new Date(filters.DateTo) : null;
      if (dateTo) {
        dateTo.setHours(23, 59, 59, 999);
      }

      return (
        (!filters.MobileUserName ||
          item.MobileUserName === filters.MobileUserName) &&
        (!filters.WhaClientName ||
          item.ClientID?.WhaClientName === filters.WhaClientName) &&
        (!filters.UnqID || item.UnqID?.toString() === filters.UnqID.toString()) &&
        (!filters.FMTID || item.FMTID === filters.FMTID) &&
        (!filters.VoucherNo || item.UnqVnos === filters.VoucherNo) &&
        (!filters?.IsSuccess ||
          item?.IsSuccess?.toString() === filters?.IsSuccess) &&
        (!filters.MobileNo || item.MobileNo === filters.MobileNo) &&
        (!filters.FirmName || item.FirmName === filters.FirmName) &&
        (!filters.ProjectName || item.ProjectName === filters.ProjectName) &&
        (!filters.StaticIP || item.StaticIP === filters.StaticIP) &&
        (!filters.DateFrom || itemDate >= new Date(filters.DateFrom)) &&
        (!dateTo || itemDate <= dateTo)
      );
    });
  }, [LogData, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      MobileUserName: "",
      WhaClientName: "",
      FMTID: "",
      UnqID: "",
      VoucherNo: "",
      IsSuccess: "",
      MobileNo: "",
      FirmName: "",
      ProjectName: "",
      StaticIP: "",
      DateFrom: null,
      DateTo: null,
    });
  };

  const columns = [
    { field: "id", headerName: "No", flex: 0.3, minWidth: 50 },
    { field: "ClientID", headerName: "Client Name", flex: 1, minWidth: 150 },
    { field: "UnqID", headerName: "Unq ID", flex: 0.5, minWidth: 80 },
    { field: "FMTID", headerName: "FMT ID", flex: 0.5, minWidth: 80 },
    {
      field: "RestResponse",
      headerName: "RestResponse",
      flex: 3,
      minWidth: 450,
      wrap: true,
      renderCell: ({ row }) => <ResponseCell text={row.RestResponse} limit={30} isAllExpanded={expandAll} />
    },
    { field: "UnqVnos", headerName: "Voucher No.", flex: 0.6, minWidth: 100 },
    {
      field: "Response",
      headerName: "Response",
      flex: 2,
      minWidth: 350,
      wrap: true,
      renderCell: ({ row }) => <ResponseCell text={row.Response} limit={25} isAllExpanded={expandAll} />
    },
    { field: "IsSuccess", headerName: "Success", flex: 0.4, minWidth: 70 },
    { field: "MobileNo", headerName: "Mobile No.", flex: 1, minWidth: 120 },
    { field: "StartFlag", headerName: "StartFlag.", flex: 0.4, minWidth: 80 },
    { field: "FirmName", headerName: "Firm Name.", flex: 1.5, minWidth: 180, wrap: true },
    { field: "ProjectName", headerName: "Project Name", flex: 1.2, minWidth: 150 },
    { field: "StaticIP", headerName: "Static IP", flex: 1, minWidth: 120 },
    { field: "WhaSendDatetime", headerName: "Time", flex: 1.2, minWidth: 150 },
    { field: "Price", headerName: "Price.", flex: 0.6, minWidth: 80 },
    { field: "PrvBal1", headerName: "Prev Bal 1", flex: 0.6, minWidth: 90 },
    { field: "PrvBal2", headerName: "Prev Bal 2", flex: 0.6, minWidth: 90 },
  ];

  const rows = useMemo(() => {
    return filterdata?.map((el, index) => ({
      id: index + 1,
      MainId: el._id,
      ClientID: el?.ClientID?.WhaClientName || "-",
      UnqID: el?.UnqID,
      FMTID: el?.FMTID,
      RestResponse: typeof el?.RestResponse === 'object' ? JSON.stringify(el?.RestResponse) : (el?.RestResponse || "-"),
      UnqVnos: el?.UnqVnos,
      Response: typeof el?.Response === 'object' ? JSON.stringify(el?.Response) : (el?.Response || "-"),
      IsSuccess: el?.IsSuccess ? "True" : "False",
      MobileNo: el?.MobileNo || "-",
      StartFlag: el?.StartFlag,
      FirmName: el?.FirmName || "-",
      ProjectName: el?.ProjectName || "-",
      StaticIP: el?.StaticIP,
      WhaSendDatetime: formatDisplayDate(el?.WhaSendDatetime),
      Price: el?.Price,
      PrvBal1: el?.PrvBal1,
      PrvBal2: el?.PrvBal2,
    })) || [];
  }, [filterdata]);

  const exportToExcel = () => {
    const sourceData = (filterdata && filterdata.length > 0) ? filterdata : (LogData || []);
    if (!sourceData || sourceData.length === 0) {
      alert("No log data available to export!");
      return;
    }

    setLoading(true);

    const dataToExport = sourceData.map((item, index) => ({
      "No": index + 1,
      "Client Name": item.ClientID?.WhaClientName || item.clientname || (typeof item.ClientID === "string" ? item.ClientID : "-"),
      "Unq ID": item.UnqID || "-",
      "FMT ID": item.FMTID || "-",
      "RestResponse": typeof item.RestResponse === "object" ? JSON.stringify(item.RestResponse) : (item.RestResponse || "-"),
      "Voucher No.": item.UnqVnos || "-",
      "Response": typeof item.Response === "object" ? JSON.stringify(item.Response) : (item.Response || "-"),
      "Success": item.IsSuccess ? "True" : "False",
      "Mobile No.": item.MobileNo || "-",
      "StartFlag": item.StartFlag || "-",
      "Mobile User Name": item.MobileUserName || "-",
      "Firm Name": item.FirmName || "-",
      "Project Name": item.ProjectName || "-",
      "Static IP": item.StaticIP || "-",
      "Time": formatDisplayDate(item.WhaSendDatetime) || item.WhaSendDatetime || "-",
      "Price": item.Price ?? "-",
      "Prev Bal 1": item.PrvBal1 ?? "-",
      "Prev Bal 2": item.PrvBal2 ?? "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Auto column widths
    worksheet["!cols"] = Object.keys(dataToExport[0] || {}).map((key) => {
      const maxLen = Math.max(
        key.length,
        ...dataToExport.slice(0, 50).map((r) => String(r[key] || "").length)
      );
      return { wch: Math.min(Math.max(maxLen + 3, 10), 45) };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(data, "Log_Report.xlsx");

    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  // Show loader while data is being fetched or not yet loaded
  if (reduxLoading || !dataLoaded || !LogData) {
    return <Loader />;
  }

  return (
    <>
      <div className="">
        <div className="title flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          {/* Title */}
          <h3 className="text-xl font-bold main_title_page text-white px-5 py-2 rounded w-full sm:w-auto text-center sm:text-left">
            Log Report
          </h3>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto items-center">
            <div className="flex items-center space-x-2 bg-[#122A47] text-white px-4 py-2 rounded-lg shadow-md w-full sm:w-auto justify-center">
              <span className="text-sm font-semibold">Expand All</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={expandAll}
                  onChange={(e) => setExpandAll(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
              </label>
            </div>

            <button
              onClick={exportToExcel}
              disabled={loading}
              className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              <IoDocumentTextSharp size={18} className="me-1" />
              {loading ? "Exporting..." : "Export to Excel"}
            </button>

            <button
              onClick={() => setIsDrawerOpen(true)}
              className="bg-gray-600 text-white px-3 py-2 rounded hover:bg-gray-700 flex items-center justify-center"
            >
              <IoFilter size={18} className="me-1" />
              Open Filters
            </button>

            <button
              onClick={clearFilters}
              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 flex items-center justify-center"
            >
              <LuLayoutGrid size={18} className="me-1" />
              Show All Log
            </button>
          </div>
        </div>

        <div className="bg-transparent rounded-lg overflow-x-auto mt-5">
          {/* Show loader if filtering is taking time with large data */}
          {filterdata && filterdata.length > 0 ? (
            <WpDynamicTable columns={columns} rows={rows} height="700px" rowsPerPage={10} />
          ) : LogData && LogData.length > 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500">No data found with the current filters.</p>
            </div>
          ) : (
            <div className="text-center py-10">
              <p className="text-gray-500">No log data available.</p>
            </div>
          )}
        </div>

        {isDrawerOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-2">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Log Filter Options
                  </h2>
                  <button
                    onClick={() => setIsDrawerOpen(false)}
                    className="text-gray-500 hover:text-gray-800 text-2xl"
                  >
                    &times;
                  </button>
                </div>
                <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Row 1 */}
                  <div>
                    <label className="text-sm font-medium">
                      Mobile User Name
                    </label>
                    <select
                      name="MobileUserName"
                      value={filters.MobileUserName}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">All</option>
                      {[...new Set(LogData?.map((item) => item.MobileUserName))]
                        .filter(Boolean)
                        .map((name, index) => (
                          <option key={index} value={name}>
                            {name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Client Name</label>
                    <select
                      name="WhaClientName"
                      value={filters.WhaClientName}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          WhaClientName: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">All</option>
                      {[
                        ...new Set(
                          LogData?.map((el) => el?.ClientID?.WhaClientName)
                        ),
                      ].map(
                        (client) =>
                          client && (
                            <option key={client} value={client}>
                              {client}
                            </option>
                          )
                      )}
                    </select>
                  </div>

                  {/* Row 2 */}
                  <div>
                    <label className="text-sm font-medium">FMT ID</label>
                    <select
                      name="FMTID"
                      value={filters.FMTID}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">All</option>
                      {[...new Set(LogData?.map((item) => item.FMTID))]
                        .filter(Boolean)
                        .map((fmt, index) => (
                          <option key={index} value={fmt}>
                            {fmt}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Unique ID</label>
                    <select
                      name="UnqID"
                      value={filters.UnqID}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">All</option>
                      {[...new Set(LogData?.map((item) => item.UnqID))]
                        .filter(Boolean)
                        .map((id, index) => (
                          <option key={index} value={id}>
                            {id}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Row 3 */}
                  <div>
                    <label className="text-sm font-medium">
                      Voucher Number
                    </label>
                    <select
                      name="VoucherNo"
                      value={filters.VoucherNo}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">All</option>
                      {[...new Set(LogData?.map((item) => item.UnqVnos))]
                        .filter(Boolean)
                        .map((voucher, index) => (
                          <option key={index} value={voucher}>
                            {voucher}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <div>
                      <label className="text-sm font-medium">Success</label>
                      <select
                        name="IsSuccess"
                        value={filters.IsSuccess}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            IsSuccess: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                      >
                        <option value="">All</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    </div>
                  </div>

                  {/* Row 4 */}
                  <div>
                    <label className="text-sm font-medium">Mobile Number</label>
                    <select
                      name="MobileNo"
                      value={filters.MobileNo}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">All</option>
                      {[...new Set(LogData?.map((el) => el?.MobileNo))].map(
                        (mobile) =>
                          mobile && (
                            <option key={mobile} value={mobile}>
                              {mobile}
                            </option>
                          )
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Firm Name</label>
                    <select
                      name="FirmName"
                      value={filters.FirmName}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">All</option>
                      {[...new Set(LogData?.map((item) => item.FirmName))]
                        .filter(Boolean)
                        .map((firm, index) => (
                          <option key={index} value={firm}>
                            {firm}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Row 5 */}
                  <div>
                    <label className="text-sm font-medium">Project Name</label>
                    <select
                      name="ProjectName"
                      value={filters.ProjectName}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">All</option>
                      {[...new Set(LogData?.map((item) => item.ProjectName))]
                        .filter(Boolean)
                        .map((project, index) => (
                          <option key={index} value={project}>
                            {project}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Static IP</label>
                    <select
                      name="StaticIP"
                      value={filters.StaticIP}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">All</option>
                      {[...new Set(LogData?.map((item) => item.StaticIP))]
                        .filter(Boolean)
                        .map((ip, index) => (
                          <option key={index} value={ip}>
                            {ip}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Row 6 */}
                  <div>
                    <label className="text-sm font-medium">From Date</label>
                    <input
                      type="date"
                      name="DateFrom"
                      value={filters.DateFrom}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">To Date</label>
                    <input
                      type="date"
                      name="DateTo"
                      value={filters.DateTo}
                      onChange={handleFilterChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    />
                  </div>
                </form>

                {/* Footer Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      clearFilters();
                      setIsDrawerOpen(false);
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WhatsappLogReport;